/**
 * Artwork per role (section 4): each shape drawn as its frame (2:3, 16:9, 1:1…), with what it is
 * used for, and Upload / Choose / Remove right there. The shapes an item needs first are shown;
 * the rest wait behind "More shapes" (the second and last level of disclosure).
 */

import { useState } from "react";
import Button from "@web-ui/components/Button";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import type { ImageRole } from "@media-domain/images";
import { explainMediaError } from "@/media/errors";
import { ImageCropUpload } from "@/media/image-upload";
import { FilePreview } from "@/media/file-preview";
import { MediaPicker } from "@/media/media-picker";
import { useUploads } from "@/media/upload-center";
import { useStudio } from "@/shell/studio";
import { useForm } from "../form-context";
import { fieldDomId, getPath } from "../paths";
import type { ArtworkRole, SectionPlan } from "../plan";

type Img = { url?: string; width?: number; height?: number; color?: string; sizes?: Array<{ width: number; url: string }> };

export function ArtworkSection({ section }: { section: Extract<SectionPlan, { id: "artwork" }> }) {
  const form = useForm();
  const filledMore = section.more.some((r) => getPath(form.doc, r.path));
  const [more, setMore] = useState(filledMore);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {section.primary.map((r) => <RoleCard key={r.path} role={r} />)}
      </div>
      {section.more.length ? (
        <>
          <div><Button type="button" size="sm" variant="ghost" icon="chevron" aria-expanded={more} onClick={() => setMore(!more)}>{more ? "Fewer shapes" : `More shapes (${section.more.map((r) => r.label.toLowerCase()).join(", ")})`}</Button></div>
          {more ? <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{section.more.map((r) => <RoleCard key={r.path} role={r} />)}</div> : null}
        </>
      ) : null}
    </div>
  );
}

function RoleCard({ role }: { role: ArtworkRole }) {
  const form = useForm();
  const { client } = useStudio();
  const { refresh, assets } = useUploads();
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState("");
  const img = getPath(form.doc, role.path) as Img | undefined;
  // The upload in Files this picture came from (same address), for its preview.
  const file = img?.url ? assets?.find((a) => a.kind === "image" && (a.feed as { url?: string } | null)?.url === img.url) : undefined;
  const src = img ? [...(img.sizes ?? [])].sort((a, b) => a.width - b.width).find((s) => s.width >= 480)?.url ?? img.sizes?.at(-1)?.url ?? img.url : undefined;
  const name = (form.doc["title"] as Record<string, string> | undefined)?.[form.locale] ?? (form.doc["name"] as string | undefined) ?? "";
  const aspect = role.aspect ?? 16 / 9;
  // Very wide and very tall frames stay a sensible size in the card.
  const frameWidth = role.aspect && role.aspect < 1 ? "w-[60%]" : "w-full";
  return (
    <div id={fieldDomId(role.path)} tabIndex={-1} className="flex flex-col gap-2 rounded-2xl bg-b-surface1 p-3 outline-none">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-body-2 font-medium">{role.label}</span>
        <span className="text-caption text-t-secondary">{role.shape}</span>
      </div>
      <div className="flex justify-center">
        <div className={`relative overflow-hidden rounded-xl border-2 ${src ? "border-transparent" : "border-dashed border-s-stroke2"} bg-b-surface3 ${frameWidth}`} style={{ aspectRatio: String(aspect), backgroundColor: img?.color }}>
          {src ? <img src={src} alt={`${role.label} of ${name}`} className={`absolute inset-0 h-full w-full ${role.aspect ? "object-cover" : "object-contain p-2"}`} loading="lazy" /> : <span className="absolute inset-0 flex items-center justify-center p-2 text-center text-caption text-t-secondary">{role.shape}</span>}
        </div>
      </div>
      <p className="text-caption text-t-secondary">{role.use}</p>
      {file ? <div><Button type="button" size="xs" variant="ghost" onClick={() => setPreviewing(true)}>Preview the crops</Button></div> : null}
      <FilePreview asset={previewing ? file ?? null : null} onClose={() => setPreviewing(false)} />
      {form.canEdit ? (
        <div className="flex flex-wrap gap-1">
          <Button type="button" size="xs" variant="secondary" icon="upload" onClick={() => setUploading(true)}>{src ? "Replace" : "Upload"}</Button>
          <Button type="button" size="xs" variant="ghost" onClick={() => setPicking(true)}>Choose</Button>
          {src ? <Button type="button" size="xs" variant="ghost" onClick={() => form.set(role.path, undefined)}>Remove</Button> : null}
        </div>
      ) : null}
      {error ? <Notice tone="critical" onDismiss={() => setError("")}>{error}</Notice> : null}
      <Modal open={uploading} onClose={() => setUploading(false)} size="md">
        <div className="flex flex-col gap-4">
          <h2 className="text-h5">{`${role.label} for “${name || "this item"}”`}</h2>
          <p className="text-body-2 text-t-secondary">{`${role.shape}. ${role.use}`}</p>
          <ImageCropUpload client={client} role={role.role as ImageRole} defaultTitle={`${name} (${role.label.toLowerCase()})`} onCancel={() => setUploading(false)}
            onDone={(feed) => { form.set(role.path, feed); setUploading(false); void refresh(); }}
            onError={(e) => { setError(explainMediaError(e)); setUploading(false); }} />
        </div>
      </Modal>
      <MediaPicker kind="image" role={role.role} open={picking} onClose={() => setPicking(false)} onPick={(a) => { if (a.feed) form.set(role.path, a.feed); }} />
    </div>
  );
}
